import React, { useState, useEffect, useContext } from "react"
import { Dropdown, DropdownToggle, DropdownMenu, Button, Input, Label, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap"
import gql from "graphql-tag"
import { useQuery } from "@apollo/client"
import moment from "moment"
import withApolloClient from "../../withApolloClient"

import ConfirmationPopup from "../../Material/ConfirmationPopup"
import { GroupsTypeAhead, RepsTypeAhead } from "./TypeAheadComponents"
import { BoardQuery } from "../Queries"
import "./BoardActionsMenu.css"
import { HasPermissionsQuery } from "../../Functions/querys"
import { CurrentUserContext } from "../../Context/CurrentUserContext"

const UpdateBoardMutation = gql`
  mutation UpdateBoardMutation($input: UpdateBoardMutationInput!) {
    updateBoard(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const BoardActionsMenu = ({ boardId, apolloClient }) => {
    const { currentUser } = useContext(CurrentUserContext)
    const [open, setOpen] = useState(false)
    const [newTitle, setNewTitle] = useState("")
    const [description, setDescription] = useState("")
    const [selectedUsers, setSelectedUsers] = useState([])
    const [selectedGroups, setSelectedGroups] = useState([])
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState("")

    const { data } = useQuery(BoardQuery, {
        variables: { id: boardId },
        fetchPolicy: "network-only",
        skip: !boardId,
    })

    const { data: permData } = useQuery(HasPermissionsQuery, {
        variables: { userPermissions: ["change_board", "delete_board"] },
        fetchPolicy: "network-only",
    })

    const boarddata = data?.board

    useEffect(() => {
        if (!boarddata) return

        setNewTitle(boarddata.title || "")
        setDescription(boarddata.description || "")
        setSelectedGroups(
            boarddata?.sharedWithGroups?.edges?.map((edge) => ({
                value: edge.node.id,
                label: edge.node.name,
            })) || []
        )
        setSelectedUsers(boarddata?.sharedWithUsers?.edges?.map((edge) => edge.node) || [])
    }, [boarddata])

    const updateBoard = async (values = {}) => {
        setLoading(true)
        setError("")
        try {
            const response = await apolloClient.mutate({
                mutation: UpdateBoardMutation,
                variables: { input: { boardId, ...values } },
                refetchQueries: ["Board", "AllBoards"],
            })
            if (!response?.data?.updateBoard?.ok) {
                setError(response?.data?.updateBoard?.errors?.[0]?.messages?.[0] || "Update failed")
                return false
            }
            return true
        } catch (err) {
            setError(err.message || "Update failed")
            return false
        } finally {
            setLoading(false)
        }
    }

    const saveDetails = async () => {
        if (!newTitle.trim()) return

        const ok = await updateBoard({
            title: newTitle.trim(),
            description: description?.trim() || "",
        })
        if (ok) {
            setOpenModal("")
            setOpen(false)
        }
    }

    const savePermissions = async () => {
        const ok = await updateBoard({
            sharedWithUserIds: selectedUsers.map((user) => user.id),
            sharedWithGroupIds: selectedGroups.map((group) => group.value),
        })
        if (ok) {
            setOpenModal("")
            setOpen(false)
        }
    }

    const handleArchive = async () => {
        const ok = await updateBoard({ isArchived: true })
        if (ok) {
            setOpenModal("")
            setOpen(false)
        }
    }

    const boardOwnerName = boarddata?.owner
        ? (boarddata.owner.firstName ? `${boarddata.owner.firstName} ${boarddata.owner.lastName || ""}`.trim() : boarddata.owner.username)
        : "--"
    const isOwner = !!(boarddata?.owner?.id && currentUser?.id && boarddata.owner.id === currentUser.id)

    if (!boardId || !permData?.hasPermissions?.includes("change_board")) return null

    return (
        <>
            <Dropdown isOpen={open} toggle={() => setOpen(!open)} direction="down">
                <DropdownToggle tag="span" style={{ cursor: "pointer" }} className="menu-trigger">
                    <i className="fa fa-ellipsis-h fa-2x" />
                </DropdownToggle>
                <DropdownMenu className="board-action-main-menu" container="body" strategy="fixed">
                    <div className="menu-item" onClick={() => { setError(""); setOpenModal("details"); setOpen(false) }}>
                        <i className="fa fa-pencil-square-o" /> Board Details
                    </div>
                    <div className="menu-item" onClick={() => { setError(""); setOpenModal("permissions"); setOpen(false) }}>
                        <i className="fa fa-shield" /> Manage Permissions
                    </div>
                    {permData?.hasPermissions.includes("delete_board") && (
                        <>
                            <hr className="m-0" />
                            <div className="menu-item text-danger" onClick={() => setOpenModal("archiveBoard")}>
                                <i className="fa fa-archive text-danger" /> Archive
                            </div>
                        </>
                    )}
                </DropdownMenu>
            </Dropdown>

            <Modal isOpen={openModal === "details"} toggle={() => !loading && setOpenModal("")} className="board-details-modal">
                <ModalHeader>Board Details</ModalHeader>
                <ModalBody>
                    <div className="board-details-card">
                        <div className="board-details-meta">
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Created By</span>
                                <span className="board-details-meta-value">{boardOwnerName}</span>
                            </div>
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Date Added</span>
                                <span className="board-details-meta-value">{boarddata?.dateAdded ? moment(boarddata.dateAdded).format("MMM D, YYYY h:mm A") : "--"}</span>
                            </div>
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Board ID</span>
                                <span className="board-details-meta-value">{boarddata?.pk || "--"}</span>
                            </div>
                        </div>

                        <FormGroup>
                            <Label className="board-details-label">Board Name</Label>
                            <Input
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                placeholder="Enter board name"
                                autoFocus
                                disabled={!isOwner}
                            />
                        </FormGroup>

                        <FormGroup className="mb-0">
                            <Label className="board-details-label">Description</Label>
                            <Input
                                type="textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add a short description for this board"
                                rows="5"
                                disabled={!isOwner}
                            />
                        </FormGroup>

                        {!isOwner && <Alert color="info" className="mb-0 mt-3">Only the board owner can edit the name and description.</Alert>}
                        {error && <Alert color="danger" className="mb-0 mt-3">{error}</Alert>}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" onClick={() => setOpenModal("")} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="button" onClick={saveDetails} disabled={loading || !isOwner}>
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={openModal === "permissions"} toggle={() => !loading && setOpenModal("")} className="board-permissions-modal">
                <ModalHeader>Manage Board Permissions</ModalHeader>
                <ModalBody>
                    <div className="board-permissions-section">
                        <Label className="board-permissions-label">Shared With Groups</Label>
                        <p className="board-permissions-help">Select the groups that should be able to access this board.</p>
                        <GroupsTypeAhead defaultValue={selectedGroups} onChange={setSelectedGroups} returnOptions />
                    </div>
                    <div className="board-permissions-section">
                        <Label className="board-permissions-label">Shared With Users</Label>
                        <p className="board-permissions-help">Select individual users who should also have access to this board.</p>
                        <RepsTypeAhead defaultValue={selectedUsers} onChange={setSelectedUsers} returnObjects />
                    </div>
                    {error && <Alert color="danger" className="mb-0">{error}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button type="button" onClick={() => setOpenModal("")} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="button" onClick={savePermissions} disabled={loading}>
                        {loading ? "Saving..." : "Save Permissions"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmationPopup
                open={openModal === "archiveBoard"}
                message={"Are you sure you want to archive this board?"}
                loading={loading}
                handleClose={() => setOpenModal("")}
                action={"Yes"}
                confirmAction={handleArchive}
                color="danger"
                error={error}
            />
        </>
    )
}

export default withApolloClient(BoardActionsMenu)
